"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import NextImage from "next/image";
import { useInView, useReducedMotion } from "framer-motion";
import type { IEventContentCard } from "@/sanity/pages/PageEventTemplate";
import { cn } from "@/lib/utils";
import styles from "./monthly-media.module.css";

const asset = (name: string) => `/monthly-update/motion/${name}`;
const u = (pixels: number) => `calc(var(--u) * ${pixels})`;

function AssetImage({ name, width, height, x = 0, y = 0, className }: { name: string; width: number; height: number; x?: number; y?: number; className?: string }) {
  return <picture className="contents">{name === "update-ring-far.svg" && <source media="(max-width: 992px)" srcSet="/monthly-update/mobile-update-ring-far.svg" />}<NextImage src={asset(name)} alt="" width={width} height={height} quality={100} className={cn(styles.assetImage, className)} style={{ left: u(x), top: u(y), width: u(width), height: "auto" }} /></picture>;
}

const recapItems = [
  { title: "Rule changes", detail: "At the big firms", badge: "Changed", icon: "recap-rule.svg", iconSize: 18.6794 },
  { title: "New platforms", detail: "This month", badge: "New", icon: "recap-platform.svg", iconSize: 19 },
  { title: "Payout trends", detail: "Last 30 days", badge: "Trending", icon: "recap-trend.svg", iconSize: 19 },
];
const germanRecapItems = recapItems.map((item, i) => ({ ...item, ...[
  { title: "Neue Regeln", detail: "Bei großen Firmen", badge: "Geändert" },
  { title: "Neue Plattformen", detail: "Diesen Monat", badge: "Neu" },
  { title: "Auszahlungstrends", detail: "Letzte 30 Tage", badge: "Im Trend" },
][i] }));

function RecapAsset({ german }: { german: boolean }) {
  const items = german ? germanRecapItems : recapItems;
  // Matching rows at either end make the upward feed wrap without a visible reset.
  const recapFeed = [items[2], ...items, ...items];
  return (
    <div className={styles.scene}>
      <div className={styles.recapViewport}>
        <div className={styles.recapFeed}>
          {recapFeed.map((item, i) => (
            <div key={i} className={styles.recapRow}>
              <div className={styles.recapHeading}>
                <div className={styles.recapIcon}><NextImage src={asset(item.icon)} alt="" width={item.iconSize} height={item.iconSize} style={{ width: u(item.iconSize), height: "auto" }} /></div>
                <div className={styles.recapText}><span>{item.title}</span><span className={styles.recapDetail}>{item.detail}</span></div>
                <span className={styles.recapBadge}>{item.badge}</span>
              </div>
              <AssetImage name="recap-line.svg" width={275} height={0.778309} x={10} y={66.8} />
              <div className={styles.recapFoot}><span>{german ? "Marktrückblick" : "Market recap"}</span><span>{german ? "Oktober" : "October"}</span></div>
            </div>
          ))}
        </div>
        <div className={styles.recapProgress}><span /></div>
      </div>
    </div>
  );
}

const updateStates = [
  { title: "New rule", icon: "update-rule.svg", labelWidth: 74 },
  { title: "Payout update", icon: "update-dollar.svg", labelWidth: 109 },
  { title: "New product", icon: "update-wallet.svg", labelWidth: 99 },
  { title: "Goes live", icon: "update-check.svg", labelWidth: 75 },
];

function UpdateAsset({ german }: { german: boolean }) {
  const states = german ? updateStates.map((state, i) => ({ ...state, ...[
    { title: "Neue Regel", labelWidth: 98 },
    { title: "Auszahlungsupdate", labelWidth: 146 },
    { title: "Neues Produkt", labelWidth: 124 },
    { title: "Geht live", labelWidth: 80 },
  ][i] })) : updateStates;
  return (
    <div className={styles.scene}>
      <div className={styles.updateRipples}>
        <AssetImage name="update-ring-inner.svg" width={266.96} height={266.96} x={36.02} y={5.48} />
        <AssetImage name="update-ring-middle.svg" width={328.624} height={328.624} x={5.19} y={-23.36} />
        <AssetImage name="update-ring-outer.svg" width={390.292} height={390.292} x={-25.63} y={-52.2} />
        <AssetImage name="update-ring-far.svg" width={442.559} height={442.559} x={-51.7796} y={-81.2796} />
      </div>
      <AssetImage name="update-track.svg" width={150} height={150} x={94.5} y={61} />
      <div className={styles.updateProgressGroup}>
        <svg viewBox="0 0 150 150" className={styles.updateProgress}>
          <circle cx="75" cy="75" r="73.125" pathLength="1" className={styles.updateStroke} />
        </svg>
        <div className={styles.updateOrbit}><AssetImage name="update-dot.svg" width={12} height={12} x={69} y={-4.125} /></div>
      </div>
      <AssetImage name="update-medal-outer.svg" width={94.8224} height={94.8224} x={122.0888} y={88.5888} />
      <AssetImage name="update-medal-inner.svg" width={85.5715} height={85.5715} x={126.71} y={93.21} />
      <div className={styles.updateMedal} />
      {states.map((state, i) => (
        <div key={state.title} data-update-stage={i} className={styles.updateState} style={{ animationDelay: `${i * 3}s` }}>
          <div className={cn(styles.updateIcon, i === 3 && styles.updateCheck)} style={i === 3 ? { animationDelay: "9s" } : undefined}><AssetImage name={state.icon} width={36} height={36} /></div>
          <div className={styles.updateLabel} style={{ left: u((339 - state.labelWidth) / 2), top: u(i === 3 ? 219 : 224), width: u(state.labelWidth) }}>{state.title}</div>
          <div className={cn(styles.updatePill, i === 3 && styles.updateLive)} style={{ left: u(i === 3 ? 143 : 129), top: u(i === 3 ? 255.7091 : 260.7091), width: u(i === 3 ? 53 : 82) }}>{german ? (i === 3 ? "1. Nov." : "Ab 1. Nov.") : (i === 3 ? "Nov 1" : "Live Nov 1")}</div>
        </div>
      ))}
    </div>
  );
}

const liveQuestions = [
  "How do the new payout rules affect me?",
  "When does the new rule go live?",
  "Can I plan my next challenge around it?",
  "Live-only offer",
];

function LiveAsset({ german }: { german: boolean }) {
  const questions = german ? [
    "Wie wirken die neuen Auszahlungsregeln auf mich?",
    "Ab wann gilt die neue Regel?",
    "Kann ich meine nächste Challenge danach planen?",
    "Live-Angebot",
  ] : liveQuestions;
  return (
    <div className={styles.scene}>
      <div className={styles.liveStack}>
        {[{ x: 20, y: 0, width: 262, height: 52.767, opacity: 0.2 }, { x: 13.44, y: 4.61, width: 275, height: 50.521, opacity: 0.12 }, { x: 6, y: 8.98, width: 291, height: 46.031, opacity: 0.23 }].map(layer => <div key={layer.x} style={{ left: u(layer.x), top: u(layer.y), width: u(layer.width), height: u(layer.height), background: `rgb(20 20 21 / ${layer.opacity})` }} />)}
      </div>
      <div className={styles.liveThumbnail}>
        <picture className="contents"><source media="(max-width: 992px)" srcSet="/monthly-update/mobile-live-thumbnail.png" /><NextImage src={asset("live-thumbnail.png")} alt="" width={1919} height={1079} quality={100} className={styles.livePhoto} /></picture>
        <div className={styles.liveShade} />
        <div className={styles.liveBadge}><NextImage src={asset("live-dot.svg")} alt="" width={6} height={6} className={styles.liveSignal} style={{ width: u(6), height: "auto" }} /><span>LIVE</span></div>
      </div>
      {questions.map((question, i) => (
        <div key={question} data-message={i} className={styles.liveMessage} style={{ animationDelay: `${i * 4}s` }}>
          <div className={styles.liveAvatar} style={{ top: u(i === 3 ? 263.1056 : 251.1056) }}>
            {i === 3 ? <div className={styles.offerAvatar}><AssetImage name="live-offer-logo.svg" width={14} height={13.3752} x={8} y={8.3124} /></div> : <AssetImage name="live-avatar.png" width={34} height={34} x={-2} y={-2} />}
          </div>
          <div className={styles.liveBubble}>
            <span className={styles.liveQuestion}>{question}</span>
            {i === 3 && <span className={styles.offerPill}>{german ? "Nur im Stream" : "Only during the stream"}</span>}
          </div>
        </div>
      ))}
      <div className={styles.liveTyping}><span /><span /><span /></div>
    </div>
  );
}

export function MonthlyCardMedia({ card, index, language }: { card: IEventContentCard; index: number; language?: string }) {
  const german = language === "de";
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const inView = useInView(ref, { amount: 0.15 });
  const reducedMotion = useReducedMotion();
  const [documentVisible, setDocumentVisible] = useState(true);
  const playing = inView && documentVisible && !reducedMotion;

  useEffect(() => {
    const syncVisibility = () => setDocumentVisible(document.visibilityState === "visible");
    syncVisibility();
    document.addEventListener("visibilitychange", syncVisibility);
    return () => document.removeEventListener("visibilitychange", syncVisibility);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) void video.play().catch(() => {});
    else video.pause();
  }, [playing, card.visualVideo?.src]);

  return (
    <div ref={ref} aria-hidden data-monthly-asset={index} data-playback={reducedMotion ? "static" : playing ? "playing" : "paused"} className={cn(styles.media, "relative aspect-[339/310] w-full overflow-hidden bg-[#f5f5f5] lg:h-[38.75rem]", index === 2 ? "lg:w-[42.625rem]" : "lg:w-[42.375rem]")} style={{ "--design-width": index === 2 ? 341 : 339, "--motion-state": playing ? "running" : "paused" } as CSSProperties}>
      {card.visualVideo?.src ? (
        <video ref={videoRef} src={card.visualVideo.src} loop muted playsInline preload="metadata" className="h-full w-full object-contain" />
      ) : card.visual?.src ? (
        <NextImage src={card.visual.src} alt="" width={339} height={310} quality={100} className="h-full w-full object-contain" />
      ) : index === 0 ? <RecapAsset german={german} /> : index === 1 ? <UpdateAsset german={german} /> : <LiveAsset german={german} />}
    </div>
  );
}
